import * as Sentry from "@sentry/react";
import { buildInfo } from "@/lib/build/buildInfo";

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Should be called once during app initialization
 */
export function initializeSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE;

  // If no DSN is provided, Sentry is disabled (dev mode or missing config)
  if (!dsn) {
    console.warn(
      "[Sentry] No DSN found. Error tracking is disabled. Set VITE_SENTRY_DSN in .env"
    );
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment,
      release: `${buildInfo.service ?? 'client'}@${buildInfo.version}+${buildInfo.commitShort ?? 'local'}`,

      // Performance Monitoring
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Session replay configuration
          maskAllText: true, // Mask sensitive text
          blockAllMedia: true, // Block images/videos
        }),
      ],

      // Performance monitoring sample rate (0.0 to 1.0)
      // 1.0 = 100% of transactions are sent
      tracesSampleRate: environment === "production" ? 0.1 : 1.0,

      // Session replay sample rate
      // Only capture 10% of sessions in production
      replaysSessionSampleRate: environment === "production" ? 0.1 : 0.0,

      // Capture 100% of sessions with errors
      replaysOnErrorSampleRate: 1.0,

      // Ignore certain errors
      ignoreErrors: [
        // Browser extensions
        "top.GLOBALS",
        // Random network errors
        "Network request failed",
        "NetworkError",
        // Cancelled requests
        "AbortError",
      ],

      // Before sending events, sanitize sensitive data
      beforeSend(event, _hint) {
        // Don't send events in development (unless you want to)
        if (environment === "development") {
          console.log("[Sentry] Event captured (not sent in dev):", event);
          return null; // Don't send in development
        }

        // Sanitize request data
        if (event.request) {
          // Remove cookies
          delete event.request.cookies;

          // Remove authorization headers
          if (event.request.headers) {
            delete event.request.headers.Authorization;
            delete event.request.headers.authorization;
            delete event.request.headers["x-api-key"];
          }

          // Sanitize query parameters
          if (event.request.query_string) {
            const queryString = typeof event.request.query_string === 'string'
              ? event.request.query_string
              : '';
            const sanitizedQuery = queryString
              .replace(/token=[^&]*/gi, "token=[REDACTED]")
              .replace(/api_key=[^&]*/gi, "api_key=[REDACTED]")
              .replace(/password=[^&]*/gi, "password=[REDACTED]");
            event.request.query_string = sanitizedQuery;
          }
        }

        // Sanitize breadcrumbs
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
            if (breadcrumb.data) {
              const sanitized = { ...breadcrumb.data };
              // Remove sensitive fields
              delete sanitized.token;
              delete sanitized.accessToken;
              delete sanitized.refreshToken;
              delete sanitized.password;
              delete sanitized.apiKey;
              delete sanitized.secret;
              return { ...breadcrumb, data: sanitized };
            }
            return breadcrumb;
          });
        }

        // Sanitize extra data
        if (event.extra) {
          const sanitizeObject = (obj: any): any => {
            if (typeof obj !== "object" || obj === null) return obj;
            const sanitized: any = Array.isArray(obj) ? [] : {};
            for (const key in obj) {
              if (
                key.toLowerCase().includes("token") ||
                key.toLowerCase().includes("password") ||
                key.toLowerCase().includes("secret") ||
                key.toLowerCase().includes("apikey")
              ) {
                sanitized[key] = "[REDACTED]";
              } else if (typeof obj[key] === "object") {
                sanitized[key] = sanitizeObject(obj[key]);
              } else {
                sanitized[key] = obj[key];
              }
            }
            return sanitized;
          };
          event.extra = sanitizeObject(event.extra);
        }

        return event;
      },

    });

    if (buildInfo.commitShort) {
      Sentry.setTag("build.commit", buildInfo.commitShort);
    }
    Sentry.setTag("build.environment", buildInfo.environment);

    console.info(
      `[Sentry] Initialized successfully (release ${buildInfo.service ?? 'client'}@${buildInfo.version}+${buildInfo.commitShort ?? 'local'})`,
    );
  } catch (error) {
    console.error("[Sentry] Failed to initialize:", error);
  }
}

/**
 * Set user context for Sentry
 */
export function setUserContext(user: {
  id: string;
  email?: string;
  username?: string;
  workspaceSlug?: string;
}): void {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });

  // Add workspace as separate context for better filtering
  if (user.workspaceSlug) {
    Sentry.setContext("workspace", {
      slug: user.workspaceSlug,
    });
  }
}

/**
 * Clear user context (e.g., on logout)
 */
export function clearUserContext(): void {
  Sentry.setUser(null);
}

/**
 * Set custom context/tags
 */
export function setContext(
  key: string,
  value: Record<string, any> | null
): void {
  Sentry.setContext(key, value);
}

/**
 * Add a breadcrumb (user action trail)
 */
export function addBreadcrumb(breadcrumb: {
  message: string;
  category?: string;
  level?: "fatal" | "error" | "warning" | "info" | "debug";
  data?: Record<string, any>;
}): void {
  Sentry.addBreadcrumb(breadcrumb);
}

/**
 * Manually capture an exception
 */
export function captureException(error: Error, context?: Record<string, any>): void {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Manually capture a message
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info",
  context?: Record<string, any>
): void {
  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
}

/**
 * Get the Sentry instance (for advanced usage)
 */
export { Sentry };
